import type * as React from "react";

import { PatientSprite, type PatientSpriteSize } from "./PatientSprite";
import type { SpriteTraits } from "./sprites";
import { cn } from "../lib/cn";

const SHOWN = 4;

/** Each sprite after the first sits this far into the one before it. */
const OVERLAP = 0.4;

export interface PatientSpritesProps extends Omit<
  React.HTMLAttributes<HTMLSpanElement>,
  "children"
> {
  /** In card order; the first pet sits on top and the rest peek out behind it. One pet is a plain `PatientSprite`. */
  sprites: readonly (SpriteTraits | null | undefined)[];
  size?: PatientSpriteSize;
  label?: string;
  ref?: React.Ref<HTMLSpanElement>;
}

/**
 * A household's pets as one stacked sprite: the same height as one
 * `PatientSprite`, wider by the pets behind. Past four, a count takes the
 * rest's place.
 */
export function PatientSprites({
  sprites,
  size = 28,
  label,
  className,
  ...rest
}: PatientSpritesProps) {
  const shown = sprites.slice(0, SHOWN);
  const more = sprites.length - shown.length;

  return (
    <span
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      {...rest}
      className={cn(
        "isolate inline-flex shrink-0 items-center gap-1",
        className,
      )}
    >
      <span className="inline-flex">
        {shown.map((sprite, i) => (
          <PatientSprite
            key={i}
            sprite={sprite}
            size={size}
            className={cn(
              "relative",
              shown.length > 1 &&
                "[filter:drop-shadow(0_0_1px_var(--color-card))_drop-shadow(0_0_1px_var(--color-card))]",
            )}
            style={{
              marginLeft: i === 0 ? 0 : -size * OVERLAP,
              zIndex: shown.length - i,
            }}
          />
        ))}
      </span>
      {more > 0 && (
        <span className="font-mono text-fine-print text-annotation">
          +{more}
        </span>
      )}
    </span>
  );
}
